import { Option } from '@sapphire/result';

import {
  FeasibilityCriterion,
  FeasibilityQuota,
  QUALIFICATION_CODE,
  Quota,
  SurveyMetadataForm,
  SurveyQualification,
} from '@ps/survey-utils';
import {
  FeasibilityCriteriaDto,
  FeasibilityRequestDto,
} from '../dtos/feasibility.dto';

export interface ComputeFeasibility {
  surveyId: number;
  user: {
    id: number;
    email: string;
    companyId: number;
  };
  metadata: SurveyMetadataForm;
  qualifications: SurveyQualification[];
  quotas: Quota[];
}

export interface FeasibilityPayloadResult {
  readonly payload: FeasibilityRequestDto;
  readonly quotaSet: ReadonlyMap<number | string, readonly FeasibilityQuota[]>;
}

// TODO: Update this once qualifications and quotas are saved
export class FeasibilityPayloadMapper {
  public build(
    data: ComputeFeasibility,
    defaultQuotaCriteria: FeasibilityCriterion[],
  ): FeasibilityPayloadResult {
    const { surveyId, user, metadata } = data;

    const quotaId = crypto.randomUUID();
    const requestCriteria = defaultQuotaCriteria.map((criterion) =>
      this._toDto(criterion),
    );

    const payload: FeasibilityRequestDto = {
      buyer_id: user.companyId,
      user_id: user.id,
      project_id: surveyId,
      completes_required: metadata.completes ?? 0,
      expected_ir: metadata?.incidence ?? 0,
      expected_loi: metadata?.loi ?? 0,
      field_time: metadata?.fieldTime ?? 0,
      survey_localization: this._localization(metadata),
      quotas: [
        {
          buyer_quota_id: quotaId,
          required_count: metadata.completes ?? 0,
          criteria: this._trimZipcode(requestCriteria, {
            clearConditionCodes: false,
          }),
        },
      ],
      defaultQuota: [
        {
          criteria: this._trimZipcode(requestCriteria, {
            clearConditionCodes: true,
          }),
        },
      ],
      offer_price: { value: null, active: false },
      survey_grouping: [],
      is_exclusion: false,
      grouping_statuses: [],
      click_balancing: Number(metadata.clickBalancing),
      is_optimized_payload: true,
    };

    // Mirrors the single combined quota above so its real feasible count
    // comes back through the response. This still isn't a true per-quota
    // breakdown — that needs `Survey.quotas` to be wired into the store.
    const [quotaCriterion] = defaultQuotaCriteria;
    const quotaSet = new Map<number | string, FeasibilityQuota[]>([
      [
        quotaCriterion.qualificationCode,
        [
          {
            ...quotaCriterion,
            buyerQuotaId: quotaId,
            requiredCount: metadata.completes ?? 0,
          },
        ],
      ],
    ]);

    return { payload, quotaSet };
  }

  private _localization(metadata: SurveyMetadataForm): string {
    const languageCode = Option.from(metadata.selectedLanguages[0]).unwrap();
    const countryCode = Option.from(metadata.selectedCountries[0]).unwrap();

    return `${languageCode}_${countryCode}`;
  }

  private _toDto(criterion: FeasibilityCriterion): FeasibilityCriteriaDto {
    return {
      qualification_code: criterion.qualificationCode,
      qualification_name: criterion.qualificationName,
      condition_codes: criterion.conditionCodes
        ? [...criterion.conditionCodes]
        : undefined,
      condition_names: criterion.conditionNames
        ? [...criterion.conditionNames]
        : undefined,
      range_sets: criterion.rangeSets?.map((range) => ({ ...range })),
    };
  }

  /**
   * ZIPCODE lists can be large, so this mapper strips them before sending: always
   * from `defaultQuota` (display-only), and from `quotas` only when already
   * present.
   */
  private _trimZipcode(
    criteria: readonly FeasibilityCriteriaDto[],
    { clearConditionCodes }: { clearConditionCodes: boolean },
  ): FeasibilityCriteriaDto[] {
    return criteria.map((criterion) => {
      if (
        criterion.qualification_code !== QUALIFICATION_CODE.ZIPCODE ||
        (!clearConditionCodes && !criterion.condition_names)
      ) {
        return criterion;
      }

      return {
        ...criterion,
        condition_names: [],
        ...(clearConditionCodes ? { condition_codes: [] } : {}),
      };
    });
  }
}
